import { Callout } from '@studio/design-system/callout';
import { Container } from '@studio/design-system/select';
import { getInfo } from '@studio/models/gamepad';
import { toAccessor } from '@studio/react-utils';
import { useMount, useShallowEffect } from '@studio/react-utils/hooks';
import { isNotEmpty } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { Column, Exhibit } from './styled.jsx';
import { Selector } from '#~/gamepad/components/selector/index.js';
import { Specimen } from '#~/gamepad/components/specimen/index.js';
import { useGamepadIds } from '#~/gamepad/hooks.js';
import { Panel as Notifications } from '#~/notifications/components/panel/index.js';
import { post } from '#~/notifications/index.js';

/** `<Gamepad>` exhibit. */
export const Gamepad = (): ReactElement => {
  const gamepadIds = useGamepadIds();

  const [selection, setSelection] = useState<T_Maybe<Gamepad['id']>>(() => gamepadIds[0]);

  // Auto-select first gamepad
  useShallowEffect(() => {
    if ((selection && !gamepadIds.includes(selection)) || (!selection && gamepadIds.length > 0)) {
      setSelection(gamepadIds[0]);
    }
  }, [gamepadIds, selection]);

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // Watch the windows's online status.
    window.addEventListener(
      'gamepadconnected',
      ({ gamepad }) => {
        const info = getInfo(gamepad);

        post({
          message: `Your ${info.name} gamepad has been connected.`,
          severity: 'success',
          title: 'Gamepad Connected'
        });
      },
      { signal }
    );

    window.addEventListener(
      'gamepaddisconnected',
      ({ gamepad }) => {
        const info = getInfo(gamepad);

        // A disconnected gamepad is a status notice with no in-app action
        // (the user reconnects the hardware), so it must not light the bell.
        post({
          message: `Your ${info.name} gamepad has been disconnected.`,
          title: 'Gamepad Disconnected'
        });
      },
      { signal }
    );

    return () => {
      abortController.abort();
    };
  });

  return (
    <>
      <Exhibit title='Gamepads'>
        <Column className='w-dialog items-center py-32'>
          <div className='w-prompt'>
            {isNotEmpty(gamepadIds) ? (
              <Container label='Connected Gamepads'>
                <Selector available={gamepadIds} selection={toAccessor(selection, setSelection)} />
              </Container>
            ) : (
              <Callout severity='warning' title='No Gamepads Detected'>
                <Callout.P>
                  Make sure your gamepad is turned on as is connected to this computer. You may need
                  to push a button on the gamepad to wake it up.
                </Callout.P>
              </Callout>
            )}
          </div>

          {!!selection && <Specimen className='m-64' gamepadId={selection} />}
        </Column>
      </Exhibit>

      <Notifications />
    </>
  );
};
